import { faQuoteLeft, faQuoteRight } from "@fortawesome/free-solid-svg-icons";
import { companyVideoPoster, companyVideoUrl } from "../../../helpers/company_video";
import { useMediaQuery } from "@mui/material";
import {
  useIntersectionObserver,
  useIsClient,
  useWindowSize,
} from "@uidotdev/usehooks";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Color from "../../../assets/colors";
import {
  BoxContainer,
  FlexContainer,
  PlayIconStyled,
  SectionContainer,
  TextStyled,
} from "../styles";
import {
  FontAwesomeIconStyled,
  QuoteCard,
  ReactPlayerStyled,
  TextContainer,
} from "./styles";


function CompanyVideos(props) {
  const { companyDetail, setCurrentTab } = props || {};
  const { t } = useTranslation();

  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isIpadPro = useMediaQuery("(max-width: 1280px)");
  const isTablet = useMediaQuery("(min-width: 640px)");

  const [ref, entry] = useIntersectionObserver({
    threshold: 0,
    rootMargin: "-200px 0px -60% 0px",
  });
  
  useEffect(() => {
    if (entry?.isIntersecting) {
      setCurrentTab(3);
    }
  }, [entry?.isIntersecting, setCurrentTab]);

  const isClient = useIsClient();
  const [hover, setHover] = useState(false);
  const [selected, setSelected] = useState(false);

  const companyVideos = companyDetail?.videos?.map((video) => {
    return {
      vimeoId: video?.vimeoId,
      quote: video?.quote,
      personName: video?.personName,
      personPosition: video?.personPosition,
      isVertical: video?.height > video?.width,
    };
  });

  // var for total number of videos
  const totalVideo = companyVideos?.length;

  // var for total number of vertical videos
  const totalVertical = companyVideos.reduce(
    (count, video) => count + (video.isVertical === true ? 1 : 0),
    0
  );

  //logic for sorting array by isVertical
  function sortArrayByIsVertical(arr) {
    arr.sort((a, b) => {
      if (a.isVertical && !b.isVertical) {
        return -1;
      } else if (!a.isVertical && b.isVertical) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  sortArrayByIsVertical(companyVideos);

  //logic for handling hover state
  function handleHover(hoverState, index) {
    setHover(hoverState);
    setSelected(index);
  }

  // condition for rendering different video layout based on amount of videos and amount of vertical videos
  // combination of layout
  // totalVideo = amount of videos and totalVertical = amount of vertical videos
  // 1.  totalVideo = 1 video and totalVertical = 0
  // 2.  totalVideo = 1 video and totalVertical = 1
  // 3.  totalVideo = 2 video and totalVertical = 0
  // 4.  totalVideo = 2 video and totalVertical = 1
  // 5.  totalVideo = 2 video and totalVertical = 2
  // 6.  totalVideo = 3 video and totalVertical = 0
  // 7.  totalVideo = 3 video and totalVertical = 1
  // 8.  totalVideo = 3 video and totalVertical = 2
  // 9.  totalVideo = 3 video and totalVertical = 3

  return isClient ? (
    <SectionContainer
      aria-label={t("companydetail.videos_section_aria")}
      id="CompanyPeople"
      ref={ref}
    >
      <TextStyled
        variant="h2"
        fontSize="1.25rem"
        fontWeight={700}
        textTransform="capitalize"
        aria-label={t("companydetail.videos_section_title_aria")}
        color={"#000"}
        marginBottom="1rem"
      >
        {t("companydetail.our_people")}
      </TextStyled>
      {isTablet ? (
        <FlexContainer
          flexDirection={totalVertical === 3 ? "row" : "column"}
          gap={isIpadPro ? "0.5rem" : isDesktop ? "1.5rem" : "1rem"}
        >
          <FlexContainer
            flexDirection={totalVertical >= 1 ? "row" : "column"}
            gap={isIpadPro ? "0.5rem" : isDesktop ? "1.5rem" : "1rem"}
          >
            <FlexContainer
              flexDirection={totalVertical === 3 ? "column" : "row"}
              alignItems={totalVertical === 1 ? "center" : null}
              gap={isDesktop ? "1.5rem" : "1rem"}
            >
              <VideoContainer
                index={0}
                video={companyVideos[0]}
                handleHover={handleHover}
                totalVertical={totalVertical}
                hover={hover}
                selected={selected}
              />
              {totalVertical === 2 ? (
                <VideoContainer
                  index={1}
                  video={companyVideos[1]}
                  handleHover={handleHover}
                  totalVertical={totalVertical}
                  hover={hover}
                  selected={selected}
                />
              ) : null}
              {!(totalVideo >= 2 && totalVertical >= 1 && totalVertical < 3) ? (
                <VideoTextCard
                  index={0}
                  video={companyVideos[0]}
                  totalVideo={totalVideo}
                  totalVertical={totalVertical}
                  handleHover={handleHover}
                  hover={hover}
                  selected={selected}
                />
              ) : null}
            </FlexContainer>
            {totalVideo > 1 ? (
              <FlexContainer
                flexDirection={totalVertical >= 1 ? "column" : "row"}
                gap={isDesktop ? "1.5rem" : "1rem"}
              >
                <FlexContainer
                  flexDirection={totalVertical === 2 ? "column" : "row"}
                  gap={isDesktop ? "1.5rem" : "1rem"}
                >
                  {totalVideo >= 2 &&
                  totalVertical >= 1 &&
                  totalVertical < 3 ? (
                    <VideoTextCard
                      index={0}
                      video={companyVideos[0]}
                      totalVideo={totalVideo}
                      totalVertical={totalVertical}
                      handleHover={handleHover}
                      hover={hover}
                      selected={selected}
                    />
                  ) : null}

                  <VideoTextCard
                    index={1}
                    video={companyVideos[1]}
                    totalVideo={totalVideo}
                    totalVertical={totalVertical}
                    handleHover={handleHover}
                    hover={hover}
                    selected={selected}
                  />
                </FlexContainer>
                {!(totalVertical === 2) ? (
                  <VideoContainer
                    index={1}
                    video={companyVideos[1]}
                    handleHover={handleHover}
                    totalVertical={totalVertical}
                    hover={hover}
                    selected={selected}
                  />
                ) : null}
              </FlexContainer>
            ) : null}
          </FlexContainer>
          {totalVideo > 2 ? (
            <FlexContainer
              flexDirection={totalVertical === 3 ? "column" : "row"}
              gap={isDesktop ? "1.5rem" : "1rem"}
            >
              <VideoContainer
                index={2}
                video={companyVideos[2]}
                handleHover={handleHover}
                totalVertical={totalVertical}
                hover={hover}
                selected={selected}
              />
              <VideoTextCard
                index={2}
                video={companyVideos[2]}
                totalVideo={totalVideo}
                totalVertical={totalVertical}
                handleHover={handleHover}
                hover={hover}
                selected={selected}
              />
            </FlexContainer>
          ) : null}
        </FlexContainer>
      ) : (
        <FlexContainer
          flexDirection={"column"}
          gap="1rem"
          justifyContent={"center"}
        >
          {companyVideos?.map((video, index) => {
            return (
              <>
                <VideoContainer video={video} index={index} />
                <VideoTextCard video={video} index={index} />
              </>
            );
          })}
        </FlexContainer>
      )}
    </SectionContainer>
  ) : null;
}

// function for rendering video text card

function VideoTextCard(props) {
  const { index, video, totalVertical, handleHover, hover, selected } = props;

  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isTablet = useMediaQuery("(min-width: 640px)");

  const getHeightStyle = useMemo(() => {
    if (index !== 2 && totalVertical < 3) {
      if (isDesktop) {
        return {
          height:
            totalVertical === 1
              ? "16rem"
              : totalVertical >= 1
              ? "19.25rem"
              : totalVertical === 3
              ? "16.25rem"
              : "22.5rem",
        };
      } else if (isTablet) {
        return {
          height:
            totalVertical >= 1
              ? "12.3rem"
              : totalVertical === 3
              ? "12.25rem"
              : "14.0625rem",
        };
      }
    } else {
      return {
        height: isDesktop ? "22.5rem" : isTablet ? "14.0625rem" : "13.75rem",
      };
    }
  }, [index, totalVertical, isDesktop, isTablet]);

  const getHoverStyle = useMemo(() => {
    const styles = [
      {
        backgroundColor: "rgba(239, 239, 253, 1)",
        border: `1.5px solid ${Color.darkPurple}`,
        color: Color.darkPurple,
      },
      {
        backgroundColor: "rgba(244, 250, 216, 1)",
        border: `1.5px solid rgba(115, 136, 17, 1)`,
        color: "rgba(115, 136, 17, 1)",
      },
      {
        backgroundColor: "rgba(251, 247, 210, 1)",
        border: `1.5px solid rgba(144, 114, 9, 1)`,
        color: "rgba(144, 114, 9, 1)",
      },
    ];

    const tabletCondition =
      !isTablet && (index === 0 || index === 1 || index === 2);

    if (
      (index < styles.length && hover && index === selected) ||
      tabletCondition
    ) {
      return styles[index];
    }

    return {};
  }, [index, hover, selected, isTablet]);

  const getFontSize = useMemo(() => {
    let fontSize;
    const textLength = video?.quote?.length;
    const height = parseFloat(getHeightStyle?.height);

    if (isDesktop) {
      if (textLength < 80) {
        fontSize = height === 19.25 || height === 16 ? 1.25 : 1.5;
      } else if (textLength < 155) {
        fontSize =
          height === 22.5 ? 1.5 : height === 19.25 || height === 16 ? 1 : 1.25;
      } else if (textLength <= 190) {
        fontSize = height === 22.5 ? 1 : height === 18.75 ? 0.875 : 1;
      } else if (textLength < 350) {
        fontSize = height === 22.5 ? 1 : 0.875;
      } else if (textLength < 500) {
        fontSize = 0.875;
      } else if (textLength >= 500) {
        fontSize = 0.8125;
      }
    } else if (isTablet) {
      switch (true) {
        case textLength <= 120:
          fontSize = 1;
          break;
        case textLength <= 150:
          fontSize = height === 11.775 ? 1 : 0.75;
          break;
        case textLength <= 165:
          fontSize = height === 14.0625 ? 1 : 0.6875;
          break;
        case textLength > 170:
          fontSize = height === 14.0625 ? 0.75 : 0.6875;
          break;
        default:
          fontSize = null;
      }
    } else {
      fontSize = 1;
    }

    return {
      fontSize: `${fontSize}rem`,
    };
  }, [video?.quote?.length, getHeightStyle, isDesktop, isTablet]);

  const videoQuote = useMemo(() => {
    const quote = video?.quote;
    const fontSize = parseFloat(getFontSize?.fontSize);

    const spaces = isDesktop
      ? "\u00A0".repeat(fontSize === 0.875 ? 7 : 6)
      : "\u00A0".repeat(fontSize === 0.6875 ? 8 : 7);

    return fontSize ? `${spaces}${quote}${spaces}` : quote;
  }, [video, getFontSize]);

  return (
    <QuoteCard
      sx={
        isDesktop
          ? { ...getHeightStyle, ...getHoverStyle }
          : isTablet
          ? getHeightStyle
          : getHoverStyle
      }
      onMouseOver={isDesktop ? () => handleHover(true, index) : null}
      onMouseLeave={isDesktop ? () => handleHover(false, index) : null}
    >
      <FlexContainer flexDirection={"column"}>
        <FontAwesomeIconStyled
          icon={faQuoteLeft}
          style={{
            alignSelf: "flex-start",
            marginBottom: isDesktop ? "-1.125rem" : "-1rem",
            fontSize: "20px",
            maxWidth: "20px",
            minWidth: "20px",
            width: "20px",
          }}
        />
        <TextContainer style={getFontSize} id="cdp-media-quote">
          {videoQuote}
        </TextContainer>
        <FontAwesomeIconStyled
          icon={faQuoteRight}
          style={{
            alignSelf: "flex-end",
            marginTop: isDesktop ? "-1.125rem" : "-1rem",
            fontSize: "20px",
            maxWidth: "20px",
            minWidth: "20px",
            width: "20px",
          }}
        />
      </FlexContainer>
      <FlexContainer flexDirection={"column"}>
        <TextStyled fontSize={isDesktop ? "1rem" : "0.875rem"} fontWeight={500}>
          {video?.personName}
        </TextStyled>
        <TextStyled fontSize={isDesktop ? "1rem" : "0.875rem"}>
          {video?.personPosition}
        </TextStyled>
      </FlexContainer>
    </QuoteCard>
  );
}

// function for rendering video container
function VideoContainer(props) {
  const { index, video, totalVertical, handleHover, hover, selected } = props;

  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isTablet = useMediaQuery("(min-width: 640px)");

  const size = useWindowSize();

  let playerWidth = 0;
  let playerHeight = 0;

  // logic for calculating player width and height for mobile
  if (!isTablet) {
    if (video?.isVertical) {
      playerWidth = size.width - 16 * 2;
      playerHeight = playerWidth / 0.5625;
    } else {
      playerWidth = size.width - 16 * 2;
      playerHeight = playerWidth * 0.5625;
    }
  } else {
    if (totalVertical === 3) {
      playerWidth = (size.width - 64) / 3;
      playerHeight = playerWidth / 0.5625;
    }
  }

  const handleMouseOver = isDesktop ? () => handleHover(true, index) : null;
  const handleMouseLeave = isDesktop ? () => handleHover(false, index) : null;

  return (
    <BoxContainer
      sx={
        hover && index !== selected
          ? { opacity: 0.5, filter: "grayscale(80%)" }
          : null
      }
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      <ReactPlayerStyled
        key={video.vimeoId}
        url={companyVideoUrl(video.vimeoId)}
        playing={true}
        loop={false}
        controls={true}
        light={companyVideoPoster(video.vimeoId)}
        playIcon={<PlayIconStyled />}
        style={{
          border: "1px solid rgba(0, 0, 0, 0.15)",
          borderRadius: "1rem",
          maxWidth: isTablet ? "auto" : "92svw",
        }}
        width={
          isTablet
            ? totalVertical === 3
              ? isDesktop
                ? 320
                : playerWidth
              : video?.isVertical
              ? isDesktop
                ? 360
                : totalVertical === 1
                ? 259
                : 225
              : isDesktop
              ? 640
              : index != 1
              ? 400
              : 440
            : playerWidth
        }
        height={
          isTablet
            ? totalVertical === 3
              ? isDesktop
                ? 565
                : playerHeight
              : video.isVertical
              ? isDesktop
                ? 640
                : totalVertical === 1
                ? 460
                : 400
              : isDesktop
              ? 360
              : index != 1
              ? 225
              : 247.5
            : playerHeight
        }
      />
    </BoxContainer>
  );
}

export default CompanyVideos;
